export class StringDeduplicator {
  private readonly items = new Map<string, number>();

  constructor(
    private readonly maxSize = 1000,
    private readonly expireTime: number | null = null,
  ) {
    if (maxSize <= 0) throw new Error('maxSize must be positive');
    if (expireTime !== null && expireTime <= 0) {
      throw new Error('expireTime must be positive or null');
    }
  }

  checkAndRecord(item: string): boolean {
    if (item === undefined || item === null) {
      throw new Error('item cannot be null or undefined');
    }

    const now = Date.now() / 1000;
    const seenAt = this.items.get(item);
    if (seenAt !== undefined && !this.isExpired(seenAt, now)) return false;
    if (seenAt !== undefined) this.items.delete(item);

    this.items.set(item, now);
    while (this.items.size > this.maxSize) {
      const oldest = this.items.keys().next().value;
      if (oldest === undefined) break;
      this.items.delete(oldest);
    }
    this.cleanup(now);
    return true;
  }

  has(item: string): boolean {
    const seenAt = this.items.get(item);
    if (seenAt === undefined) return false;
    if (!this.isExpired(seenAt, Date.now() / 1000)) return true;
    this.items.delete(item);
    return false;
  }

  clear(): void {
    this.items.clear();
  }

  delete(item: string): boolean {
    return this.items.delete(item);
  }

  size(): number {
    this.cleanup(Date.now() / 1000);
    return this.items.size;
  }

  getExpireTime(): number | null {
    return this.expireTime;
  }

  getMaxSize(): number {
    return this.maxSize;
  }

  private isExpired(seenAt: number, now: number): boolean {
    return this.expireTime !== null && now - seenAt >= this.expireTime;
  }

  private cleanup(now: number): void {
    if (this.expireTime === null) return;
    for (const [item, seenAt] of this.items) {
      if (this.isExpired(seenAt, now)) this.items.delete(item);
    }
  }
}
